import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { ArrowLeft, ChefHat, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tag } from '@/components/ui/tag';
import NutritionMetricStatusCard from '@/components/shared/NutritionMetricStatusCard';
import { CircularProgress } from '@/components/ui/circular-progress';
import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';
import cupIcon from '@/assets/images/cup.svg';
import { getDraftById } from '@/lib/apiService';
import { showErrorToast } from '@/utils/toast-utils';
import { getDietaryDisplayName } from '@/data/dietary-mapping';
import type { RecipeDetails, RecipeIngredient, RecipeNutrition, NutrientValue } from '@/types/recipe-details';
import { formatMacro } from '@/utils/gram-ml';
import RecipeIngredientCards from '@/components/recipes/RecipeIngredientCards';

const DEFAULT_NUTRITION_UNITS: Record<string, string> = {
  calories: 'kcal',
  protein: 'g',
  carbohydrate: 'g',
  total_fat: 'g',
  dietary_fibre: 'g',
  sugar: 'g',
  sodium: 'mg',
  cholesterol: 'mg',
  saturated_fat: 'g',
  trans_fat: 'g',
  mufa: 'g',
  pufa: 'g',
  vitamin_a: 'mcg RAE',
  vitamin_c: 'mg',
  vitamin_d: 'mcg',
  vitamin_e: 'mg',
  vitamin_k: 'mcg',
  vitamin_b1: 'mg',
  vitamin_b2: 'mg',
  vitamin_b3: 'mg',
  vitamin_b6: 'mg',
  vitamin_b9: 'mcg DFE',
  vitamin_b12: 'mcg',
  iron: 'mg',
  zinc: 'mg',
  calcium: 'mg',
  magnesium: 'mg',
  phosphorus: 'mg',
  potassium: 'mg',
};

const num = (o: Record<string, unknown>, k: string): number => (o[k] != null ? Number(o[k]) : 0);
const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : String(entry ?? '').trim()))
    .filter((entry) => entry.length > 0);
};

const isInternalDisplayTag = (value: string): boolean => (
  value.trim().toLowerCase().replace(/[-\s]+/g, '_') === 'internal'
);

const visibleTags = (...values: unknown[]): string[] => {
  const seen = new Set<string>();
  const tags: string[] = [];

  values.forEach((value) => {
    asStringArray(value).forEach((entry) => {
      const key = entry.toLowerCase().replace(/[-\s]+/g, '_');
      if (!key || key === 'internal' || seen.has(key)) return;
      seen.add(key);
      tags.push(entry);
    });
  });

  return tags;
};

const resolveNutritionUnit = (
  nutritionUnits: Record<string, unknown> | undefined,
  key: string,
  fallbackUnit: string,
): string => {
  const explicitUnit = nutritionUnits?.[key];
  if (typeof explicitUnit === 'string' && explicitUnit.trim().length > 0) {
    return explicitUnit.trim();
  }
  return DEFAULT_NUTRITION_UNITS[key] ?? fallbackUnit;
};

const nutrient = (
  flat: Record<string, unknown>,
  nutritionUnits: Record<string, unknown> | undefined,
  key: string,
  fallbackUnit: string,
): NutrientValue => ({
  quantity: num(flat, key),
  unit: resolveNutritionUnit(nutritionUnits, key, fallbackUnit),
});

const flatNutritionToRecipeNutrition = (
  flat: Record<string, unknown>,
  nutritionUnits?: Record<string, unknown>,
): RecipeNutrition => ({
  calories: nutrient(flat, nutritionUnits, 'calories', 'kcal'),
  macros: {
    protein: nutrient(flat, nutritionUnits, 'protein', 'g'),
    carbs: nutrient(flat, nutritionUnits, 'carbohydrate', 'g'),
    fat: nutrient(flat, nutritionUnits, 'total_fat', 'g'),
    fiber: nutrient(flat, nutritionUnits, 'dietary_fibre', 'g'),
    sugar: nutrient(flat, nutritionUnits, 'sugar', 'g'),
    sodium: nutrient(flat, nutritionUnits, 'sodium', 'mg'),
    cholesterol: nutrient(flat, nutritionUnits, 'cholesterol', 'mg'),
    saturatedFat: nutrient(flat, nutritionUnits, 'saturated_fat', 'g'),
    transFat: nutrient(flat, nutritionUnits, 'trans_fat', 'g'),
    monounsaturatedFat: nutrient(flat, nutritionUnits, 'mufa', 'g'),
    polyunsaturatedFat: nutrient(flat, nutritionUnits, 'pufa', 'g'),
  },
  micros: {
    vitamins: {
      vitaminA: nutrient(flat, nutritionUnits, 'vitamin_a', 'mcg RAE'),
      vitaminC: nutrient(flat, nutritionUnits, 'vitamin_c', 'mg'),
      vitaminD: nutrient(flat, nutritionUnits, 'vitamin_d', 'mcg'),
      vitaminE: nutrient(flat, nutritionUnits, 'vitamin_e', 'mg'),
      vitaminK: nutrient(flat, nutritionUnits, 'vitamin_k', 'mcg'),
      thiamin: nutrient(flat, nutritionUnits, 'vitamin_b1', 'mg'),
      riboflavin: nutrient(flat, nutritionUnits, 'vitamin_b2', 'mg'),
      niacin: nutrient(flat, nutritionUnits, 'vitamin_b3', 'mg'),
      vitaminB6: nutrient(flat, nutritionUnits, 'vitamin_b6', 'mg'),
      folate: nutrient(flat, nutritionUnits, 'vitamin_b9', 'mcg DFE'),
      vitaminB12: nutrient(flat, nutritionUnits, 'vitamin_b12', 'mcg'),
    },
    minerals: {
      iron: nutrient(flat, nutritionUnits, 'iron', 'mg'),
      zinc: nutrient(flat, nutritionUnits, 'zinc', 'mg'),
      calcium: nutrient(flat, nutritionUnits, 'calcium', 'mg'),
      magnesium: nutrient(flat, nutritionUnits, 'magnesium', 'mg'),
      phosphorus: nutrient(flat, nutritionUnits, 'phosphorus', 'mg'),
      potassium: nutrient(flat, nutritionUnits, 'potassium', 'mg'),
    },
  },
});

const normalizeCustomizedRecipeResponse = (data: unknown): RecipeDetails => {
  const d = data as Record<string, unknown> | null;
  const fallback = { ingredients: [], nutritionDetails: null } as unknown as RecipeDetails;
  if (!d) return fallback;

  const custom = d.customization as Record<string, unknown> | undefined;
  const recipe = d.recipe as Record<string, unknown> | undefined;

  const pickPositiveInt = (...values: unknown[]): number | undefined => {
    for (const value of values) {
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric > 0) {
        return Math.round(numeric);
      }
    }
    return undefined;
  };

  const prepTimeMinutes = pickPositiveInt(
    d.prepTimeMinutes,
    d.prepMinutes,
    recipe?.prepTimeMinutes,
    recipe?.prepMinutes,
  );
  const cookTimeMinutes = pickPositiveInt(
    d.cookTimeMinutes,
    d.cookMinutes,
    recipe?.cookTimeMinutes,
    recipe?.cookMinutes,
  );
  const totalTimeMinutes = pickPositiveInt(
    d.totalTimeMinutes,
    d.totalMinutes,
    d.readyInMinutes,
    recipe?.totalTimeMinutes,
    recipe?.totalMinutes,
    recipe?.readyInMinutes,
  ) ?? (
    (prepTimeMinutes ?? 0) + (cookTimeMinutes ?? 0) > 0
      ? (prepTimeMinutes ?? 0) + (cookTimeMinutes ?? 0)
      : undefined
  );
  const readyInMinutes = pickPositiveInt(
    d.readyInMinutes,
    recipe?.readyInMinutes,
    totalTimeMinutes,
  );

  const nutritionCandidates = [
    {
      values: asRecord(custom?.nutrition),
      units: asRecord(custom?.nutritionUnits),
    },
    {
      values: asRecord(d.nutrition),
      units: asRecord(d.nutritionUnits),
    },
    {
      values: asRecord(custom?.originalNutrition),
      units: asRecord(custom?.originalNutritionUnits) ?? asRecord(recipe?.nutritionUnits),
    },
    {
      values: asRecord(recipe?.nutritionPerServing),
      units: asRecord(recipe?.nutritionUnits),
    },
  ];
  const selectedNutrition = nutritionCandidates.find((candidate) => candidate.values);
  const flatNut = selectedNutrition?.values;
  const nutritionUnits = selectedNutrition?.units;

  const customIngredients = (custom?.finalIngredients ?? custom?.ingredients) as Array<Record<string, unknown>> | undefined;
  const ingredients: RecipeIngredient[] = Array.isArray(customIngredients)
    ? customIngredients.map((ing, idx) => ({
      id: (ing.recipeIngredientId as string) ?? (ing.ingredientId as string) ?? (ing.id as string) ?? (ing._id as string) ?? `ing-${ing.name}-${idx}`,
      name: (ing.name as string) ?? '',
      amount: Number(ing.amount) || 0,
      unit: (ing.unit as string) ?? (ing.servingId as string) ?? '',
      originalString: `${ing.amount ?? ''} ${ing.unit ?? ing.servingId ?? ''} ${ing.name ?? ''}`.trim(),
    }))
    : (d.ingredients as RecipeIngredient[]) ?? [];

  const nutritionDetails = flatNut && typeof flatNut === 'object'
    ? flatNutritionToRecipeNutrition(flatNut, nutritionUnits)
    : (d.nutritionDetails as RecipeNutrition) ?? (d.nutrition as RecipeNutrition) ?? undefined;

  const originalServings = (recipe?.originalServings as number | undefined) ?? (custom?.servings as number | undefined) ?? (d.servings as number | undefined);
  const nutritionServings =
    (custom?.nutritionServings as number | undefined)
    ?? (d.nutritionServings as number | undefined);

  return {
    ...(d as unknown as RecipeDetails),
    ingredients: ingredients.length > 0 ? ingredients : (d.ingredients as RecipeIngredient[]) ?? [],
    nutritionDetails,
    readyInMinutes: readyInMinutes ?? 0,
    totalTimeMinutes: totalTimeMinutes ?? readyInMinutes ?? 0,
    prepTimeMinutes: prepTimeMinutes ?? null,
    cookTimeMinutes: cookTimeMinutes ?? null,
    originalServings: typeof originalServings === 'number' ? originalServings : 1,
    nutritionServings:
      typeof nutritionServings === 'number' && Number.isFinite(nutritionServings) && nutritionServings > 0
        ? nutritionServings
        : undefined,
  };
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatTime = (minutes: number) => {
  const value = Math.max(0, Number(minutes) || 0);
  if (!value) return '0 min';
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const mins = value % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const formatLabel = (value: string): string =>
  value
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const humanMealName = (value: string) => {
  const cleaned = value.replace(/_\d+$/, '').replace(/[-_]+/g, ' ').trim();
  return cleaned ? formatLabel(cleaned) : 'Meal';
};

const normalizeTextList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === 'string') return entry.trim();
        if (entry && typeof entry === 'object') {
          const record = entry as Record<string, unknown>;
          const candidate = record.step ?? record.instruction ?? record.text ?? record.title;
          return typeof candidate === 'string' ? candidate.trim() : '';
        }
        return '';
      })
      .filter((entry) => entry.length > 0);
  }

  if (typeof value === 'string') {
    return value
      .split(/\r?\n+/)
      .map((entry) => entry.replace(/^-+\s*/, '').trim())
      .filter((entry) => entry.length > 0);
  }

  return [];
};

const percent = (value: number, total: number): number => (total > 0 ? (value / total) * 100 : 0);

const unwrapDraftData = (value: unknown): Record<string, unknown> | undefined => {
  const record = asRecord(value);
  const wrappedData = asRecord(record?.data);
  return wrappedData ?? record;
};

const getObjectArray = (value: unknown): Array<Record<string, unknown>> => {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is Record<string, unknown> => Boolean(asRecord(entry)));
};

const findDraftRecipeDetails = (
  draftPayload: unknown,
  recipeId: string,
  dayNumber: number,
  mealOrder: number,
): Record<string, unknown> | null => {
  const data = unwrapDraftData(draftPayload);
  if (!data) return null;

  const day = getObjectArray(data.daySuggestions)
    .find((candidate) => toNumber(candidate.dayNumber) === dayNumber);
  if (!day) return null;

  const meal = getObjectArray(day.meals)
    .find((candidate) => toNumber(candidate.mealOrder) === mealOrder);
  if (!meal) return null;

  const normalizedRecipeId = recipeId.trim();
  const recipe = getObjectArray(meal.recipeSuggestions)
    .find((candidate) => String(candidate.id ?? '').trim() === normalizedRecipeId);

  return recipe ?? null;
};

const AIMealPlanRecipeDetailsPage = () => {
  const { clientId, draftId, recipeId } = useParams<{ clientId: string; draftId: string; recipeId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rawRecipeData, setRawRecipeData] = useState<Record<string, unknown> | null>(null);
  const [recipeDetails, setRecipeDetails] = useState<RecipeDetails | null>(null);

  const mealName = searchParams.get('mealName') ?? '';
  const recipeNameFromQuery = (searchParams.get('recipeName') ?? '').trim();
  const source = (searchParams.get('source') ?? '').trim();
  const dayNumber = toNumber(searchParams.get('day'));
  const mealOrder = toNumber(searchParams.get('mealOrder'));

  const fallbackBackPath =
    draftId && clientId ? `/ai-meal-plan/draft/${draftId}/client/${clientId}` : '/meal-planning';

  const handleBack = useCallback(() => {
    if (dayNumber > 0) {
      const backSearchParams = new URLSearchParams();
      backSearchParams.set('day', String(dayNumber));
      navigate(`${fallbackBackPath}?${backSearchParams.toString()}`);
      return;
    }

    navigate(fallbackBackPath);
  }, [dayNumber, fallbackBackPath, navigate]);

  const loadRecipeDetails = useCallback(async () => {
    if (!recipeId || !draftId) return;
    if (dayNumber <= 0 || mealOrder <= 0) {
      setLoading(false);
      setError('Missing recipe context. Please open this page from the AI meal plan draft.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const draftResponse = await getDraftById(draftId);
      const draftRecipe = findDraftRecipeDetails(
        draftResponse?.data ?? draftResponse,
        recipeId,
        dayNumber,
        mealOrder,
      );

      if (!draftRecipe) {
        throw new Error('Recipe data is unavailable in this draft.');
      }

      setRawRecipeData(draftRecipe);
      setRecipeDetails(normalizeCustomizedRecipeResponse(draftRecipe));
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : 'Failed to load recipe details';
      setError(message);
      showErrorToast(loadError, 'Recipe load failed');
    } finally {
      setLoading(false);
    }
  }, [dayNumber, draftId, mealOrder, recipeId]);

  useEffect(() => {
    void loadRecipeDetails();
  }, [loadRecipeDetails]);

  const normalizedRecipeName =
    recipeDetails?.title?.trim()
    || recipeDetails?.recipeName?.trim()
    || recipeNameFromQuery
    || 'Recipe';

  const normalizedMealName = humanMealName(mealName);
  const description = useMemo(() => {
    const candidates = [
      rawRecipeData?.description,
      rawRecipeData?.summary,
      (rawRecipeData?.recipe as Record<string, unknown> | undefined)?.description,
      (rawRecipeData?.recipe as Record<string, unknown> | undefined)?.summary,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }
    return 'No description available for this recipe yet.';
  }, [rawRecipeData]);

  const imageUrl =
    recipeDetails?.recipeImageUrl?.trim()
    || recipeDetails?.image?.trim()
    || DEFAULT_RECIPE_IMAGE;

  const cuisines = useMemo(
    () => visibleTags(recipeDetails?.cuisineTypes, recipeDetails?.cuisines),
    [recipeDetails?.cuisineTypes, recipeDetails?.cuisines]
  );

  const allergens = useMemo(
    () => visibleTags(rawRecipeData?.allergens, rawRecipeData?.allergenTagIds, rawRecipeData?.allergyTags, recipeDetails?.cautions),
    [rawRecipeData?.allergens, rawRecipeData?.allergenTagIds, rawRecipeData?.allergyTags, recipeDetails?.cautions]
  );

  const intolerances = useMemo(
    () => visibleTags(rawRecipeData?.intoleranceTags, rawRecipeData?.intolerances, recipeDetails?.healthLabels),
    [rawRecipeData?.intoleranceTags, rawRecipeData?.intolerances, recipeDetails?.healthLabels]
  );
  const visibleSourceLabel = source && !isInternalDisplayTag(source) ? formatLabel(source) : '';

  const instructions = useMemo(() => {
    const customization = rawRecipeData?.customization as Record<string, unknown> | undefined;
    return normalizeTextList(
      customization?.finalInstructions
      ?? customization?.instructions
      ?? rawRecipeData?.cookingInstructions
      ?? rawRecipeData?.instructions
      ?? recipeDetails?.cookingInstructions
    );
  }, [rawRecipeData, recipeDetails?.cookingInstructions]);

  const cookingTips = useMemo(
    () =>
      normalizeTextList(
        rawRecipeData?.cookingTips
        ?? (rawRecipeData?.recipe as Record<string, unknown> | undefined)?.cookingTips
      ),
    [rawRecipeData]
  );

  const ingredientCards = useMemo(() => {
    if (recipeDetails?.ingredients?.length) {
      return recipeDetails.ingredients.map((ingredient) => {
        const qty = toNumber(ingredient.amount);
        const quantityText = qty > 0 ? formatMacro(qty) : '';
        const unitText = String(ingredient.unit ?? '').trim();
        const fullQty = `${quantityText}${quantityText && unitText ? ' ' : ''}${unitText}`.trim();

        return {
          quantity: fullQty || '-',
          name: ingredient.name?.trim() || '-',
        };
      });
    }

    const ingredientLines = asStringArray(rawRecipeData?.ingredientLines);
    return ingredientLines.map((line) => {
      const parts = line.split(' ');
      const quantity = parts.slice(0, 2).join(' ').trim();
      const name = parts.slice(2).join(' ').trim() || line;
      return { quantity: quantity || '-', name: name || '-' };
    });
  }, [rawRecipeData?.ingredientLines, recipeDetails?.ingredients]);

  const nutrition = recipeDetails?.nutritionDetails ?? recipeDetails?.nutrition;

  const calories = toNumber(nutrition?.calories?.quantity);
  const carbs = toNumber(nutrition?.macros?.carbs?.quantity);
  const protein = toNumber(nutrition?.macros?.protein?.quantity);
  const fat = toNumber(nutrition?.macros?.fat?.quantity);
  const sugar = toNumber(nutrition?.macros?.sugar?.quantity);
  const topLevelNutrition = asRecord(rawRecipeData?.nutrition);
  const customizationNutrition = asRecord(asRecord(rawRecipeData?.customization)?.nutrition);
  const alcohol =
    toNumber(topLevelNutrition?.alcohol)
    || toNumber(topLevelNutrition?.ethyl_alcohol)
    || toNumber(customizationNutrition?.alcohol)
    || toNumber(customizationNutrition?.ethyl_alcohol);
  const saturatedFat = toNumber(nutrition?.macros?.saturatedFat?.quantity);
  const monoFat = toNumber(nutrition?.macros?.monounsaturatedFat?.quantity);
  const polyFat = toNumber(nutrition?.macros?.polyunsaturatedFat?.quantity);
  const transFat = toNumber(nutrition?.macros?.transFat?.quantity);

  const carbKcal = carbs * 4;
  const proteinKcal = protein * 4;
  const fatKcal = fat * 9;
  const alcoholKcal = alcohol * 7;
  const sugarKcal = sugar * 4;

  const totalMacroKcal = carbKcal + proteinKcal + fatKcal + alcoholKcal;
  const totalKcal = calories > 0 ? calories : totalMacroKcal;

  const carbPct = percent(carbKcal, totalMacroKcal);
  const proteinPct = percent(proteinKcal, totalMacroKcal);
  const fatPct = percent(fatKcal, totalMacroKcal);
  const alcoholPct = percent(alcoholKcal, totalMacroKcal);
  const sugarPct = percent(sugarKcal, totalMacroKcal);

  const totalTime = toNumber(
    recipeDetails?.totalTimeMinutes
    || recipeDetails?.readyInMinutes
    || (toNumber(recipeDetails?.prepTimeMinutes) + toNumber(recipeDetails?.cookTimeMinutes))
  );
  const servings = toNumber(recipeDetails?.nutritionServings ?? recipeDetails?.servings ?? 1);
  const cookingMethodRaw =
    (rawRecipeData?.cookingMethod as string | undefined)
    ?? ((rawRecipeData?.recipe as Record<string, unknown> | undefined)?.cookingMethod as string | undefined)
    ?? '';
  const cookingMethodLabel = cookingMethodRaw ? formatLabel(cookingMethodRaw) : 'Mixed';

  if (loading) {
    return (
      <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: 'calc(100vh - 7.5rem)',
            borderRadius: '22px',
            border: '1px solid #BDE7F5',
            bgcolor: '#fff',
          }}
        >
          <div className="flex min-h-[calc(100vh-8rem)] flex-col items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
            <p className="text-sm text-slate-500">Loading recipe details...</p>
          </div>
        </Paper>
      </Box>
    );
  }

  if (error || !recipeDetails) {
    return (
      <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: 'calc(100vh - 7.5rem)',
            borderRadius: '22px',
            border: '1px solid #BDE7F5',
            bgcolor: '#fff',
          }}
        >
          <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center p-6">
            <Card className="w-full max-w-xl border-slate-200 shadow-sm">
              <CardContent className="p-8 text-center">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
                  <ChefHat className="h-8 w-8 text-red-500" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">Unable to open this recipe</h2>
                <p className="mt-2 text-sm text-slate-500">{error ?? 'Recipe data is unavailable.'}</p>
                <Button className="mt-6 bg-cyan-500 text-white hover:bg-cyan-600" onClick={handleBack}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Go Back
                </Button>
              </CardContent>
            </Card>
          </div>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '22px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#FFFFFF',
        }}
      >
        <div className="min-h-full bg-white p-3 sm:p-4">
          <div className="mx-auto max-w-[1320px] rounded-[26px] bg-white p-4 sm:p-6 lg:p-7">
            <button
              type="button"
              onClick={handleBack}
              className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-slate-700 transition-colors hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </button>

            <div className="mb-6 overflow-hidden rounded-[18px] bg-white p-4 ring-1 ring-inset ring-[#0000001A] sm:p-5">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1fr)]">
                <div className="min-w-0">
                  <img
                    src={imageUrl}
                    alt={normalizedRecipeName}
                    className="h-[240px] w-full rounded-[18px] object-cover sm:h-[300px] lg:h-[360px]"
                    onError={(event) => {
                      const target = event.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = DEFAULT_RECIPE_IMAGE;
                    }}
                  />
                </div>

                <div className="flex min-w-0 flex-col gap-4">
                  <div>
                    <h1 className="text-[28px] font-medium leading-[34px] text-slate-900">{normalizedRecipeName}</h1>
                    <p className="mt-1 text-xs text-slate-500">
                      Day {dayNumber} • {normalizedMealName}
                      {visibleSourceLabel ? ` • ${visibleSourceLabel}` : ''}
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-2 text-[18px] font-semibold text-slate-900">Description:</h3>
                    <p className="text-sm leading-6 text-slate-600">{description}</p>
                  </div>

                  <div className="overflow-hidden rounded-[14px] bg-white p-4 ring-1 ring-inset ring-[#0000001A]">
                    <div className="mb-4 flex items-center gap-2">
                      <img src={cupIcon} alt="Tags" className="h-6 w-6" />
                      <h3 className="text-sm font-medium text-slate-900">Tags</h3>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Cuisine</p>
                        <p className="text-sm text-slate-700">
                          {cuisines.length > 0
                            ? cuisines
                              .map((item) => getDietaryDisplayName(item, 'cuisineTypes'))
                              .join(', ')
                            : '—'}
                        </p>
                      </div>

                      <div>
                        <p className="mb-2 text-xs font-bold text-black">Allergens and intolerances present in this recipe.</p>
                        {allergens.length > 0 || intolerances.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {allergens.slice(0, 6).map((item) => (
                              <Tag
                                key={`allergy-${item}`}
                                variant="neutral"
                                size="sm"
                              >
                                {getDietaryDisplayName(item, 'allergies')}
                              </Tag>
                            ))}
                            {intolerances.slice(0, 6).map((item) => (
                              <Tag
                                key={`intolerance-${item}`}
                                variant="neutral"
                                size="sm"
                              >
                                {getDietaryDisplayName(item, 'healthLabels')}
                              </Tag>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">No major allergens or intolerances listed for this recipe.</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6 rounded-[18px] border border-[#0000001A] p-4 sm:p-5">
              <div className="mb-4 flex items-center gap-2">
                <img src={cupIcon} alt="Quick info" className="h-6 w-6" />
                <h3 className="text-[22px] font-medium text-slate-900">Quick Info</h3>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Time</p>
                  <p className="text-[15px] font-medium text-slate-800">{formatTime(totalTime)}</p>
                </div>
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Cooking Method</p>
                  <p className="text-[15px] font-medium text-slate-800">{cookingMethodLabel}</p>
                </div>
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Servings</p>
                  <p className="text-[15px] font-medium text-slate-800">{formatMacro(servings)}</p>
                </div>
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Ingredients</p>
                  <p className="text-[15px] font-medium text-slate-800">{ingredientCards.length}</p>
                </div>
              </div>
            </div>

            <div className="mb-6 rounded-[18px] border border-[#0000001A] p-4 sm:p-5">
              <h3 className="mb-4 text-[22px] font-semibold text-slate-900">Ingredients:</h3>
              <RecipeIngredientCards items={ingredientCards} />
            </div>

            <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-[18px] border border-[#0000001A] p-4 sm:p-5">
                <div className="mb-4 flex items-center gap-2">
                  <ChefHat className="h-5 w-5 text-slate-800" />
                  <h3 className="text-[20px] font-semibold text-slate-900">Instructions:</h3>
                </div>

                {instructions.length > 0 ? (
                  <ul className="space-y-2">
                    {instructions.map((step, index) => (
                      <li key={`${index}-${step.slice(0, 12)}`} className="flex items-start gap-3">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                        <span className="text-sm leading-6 text-slate-700">{step}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500">No instructions available.</p>
                )}
              </div>

              <div className="rounded-[18px] border border-[#0000001A] p-4 sm:p-5">
                <div className="mb-4 flex items-center gap-2">
                  <ChefHat className="h-5 w-5 text-slate-800" />
                  <h3 className="text-[20px] font-semibold text-slate-900">Cooking Tips:</h3>
                </div>

                {cookingTips.length > 0 ? (
                  <ul className="space-y-2">
                    {cookingTips.map((tip, index) => (
                      <li key={`${index}-${tip.slice(0, 12)}`} className="flex items-start gap-3">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                        <span className="text-sm leading-6 text-slate-700">{tip}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500">No cooking tips added for this recipe.</p>
                )}
              </div>
            </div>

            <div className="rounded-[18px] border border-[#0000001A] p-4 sm:p-5">
              <h3 className="mb-2 text-[22px] font-semibold text-slate-900">Nutrition (per serving)</h3>
              <p className="mb-4 text-xs text-slate-500">Current intake breakdown</p>

              <div className="mb-2 flex flex-col gap-1 text-xs font-medium text-slate-500 sm:flex-row sm:items-center sm:justify-between">
                <span>Energy Distribution (% kcal)</span>
                <span>Total: ~{formatMacro(totalKcal)} kcal</span>
              </div>

              <div className="mb-3 flex h-7 w-full overflow-hidden rounded-md border border-slate-200">
                {totalMacroKcal > 0 ? (
                  <>
                    {carbPct > 0 && (
                      <div
                        style={{ width: `${carbPct}%`, backgroundColor: '#60C6D9' }}
                        className="flex min-w-[42px] items-center justify-center text-xs font-bold text-white"
                      >
                        {Math.round(carbPct)}%
                      </div>
                    )}
                    {proteinPct > 0 && (
                      <div
                        style={{ width: `${proteinPct}%`, backgroundColor: '#3B82F6' }}
                        className="flex min-w-[42px] items-center justify-center text-xs font-bold text-white"
                      >
                        {Math.round(proteinPct)}%
                      </div>
                    )}
                    {fatPct > 0 && (
                      <div
                        style={{ width: `${fatPct}%`, backgroundColor: '#6366F1' }}
                        className="flex min-w-[42px] items-center justify-center text-xs font-bold text-white"
                      >
                        {Math.round(fatPct)}%
                      </div>
                    )}
                    {alcoholPct > 0 && (
                      <div
                        style={{ width: `${alcoholPct}%`, backgroundColor: '#9C27B0' }}
                        className="flex min-w-[42px] items-center justify-center text-xs font-bold text-white"
                      >
                        {Math.round(alcoholPct)}%
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex w-full items-center justify-center text-xs font-medium text-slate-500">
                    No nutrition breakdown available
                  </div>
                )}
              </div>

              <div className="mb-4 flex flex-wrap gap-4 text-xs text-slate-500">
                {carbPct > 0 && <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#60C6D9]" />Carbohydrates</div>}
                {proteinPct > 0 && <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#3B82F6]" />Protein</div>}
                {fatPct > 0 && <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#6366F1]" />Fat</div>}
                {alcoholPct > 0 && <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#9C27B0]" />Alcohol</div>}
              </div>

              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
                <NutritionMetricStatusCard label="Carbohydrates" value={carbs} percentage={carbPct} color="#60C6D9" />
                <NutritionMetricStatusCard label="Protein" value={protein} percentage={proteinPct} color="#3B82F6" />
                <NutritionMetricStatusCard label="Sugars" value={sugar} percentage={sugarPct} color="#EF4444" />
                <NutritionMetricStatusCard label="Alcohol" value={alcohol} percentage={alcoholPct} color="#9C27B0" />
              </div>

              <div className="flex flex-col gap-4 rounded-[16px] border border-[#0000001A] p-4 lg:flex-row lg:items-center">
                <div className="flex min-w-[230px] items-center gap-4">
                  <CircularProgress percentage={Math.min(fatPct, 100)} color="#6366F1" size={54} strokeWidth={5} />
                  <div>
                    <p className="text-[13px] font-bold text-slate-900">Total Fat</p>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-xs text-slate-500">{formatMacro(fat)}g</span>
                      <span className="text-xs font-bold text-slate-700">{Math.round(fatPct)}%</span>
                    </div>
                  </div>
                </div>

                <div className="flex min-w-0 flex-1 flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
                  {saturatedFat > 0 && <span>Saturated Fat <span className="font-semibold text-slate-700">{formatMacro(saturatedFat)}g</span></span>}
                  {transFat > 0 && <span>Trans Fat <span className="font-semibold text-slate-700">{formatMacro(transFat)}g</span></span>}
                  {monoFat > 0 && <span>Monounsaturated <span className="font-semibold text-slate-700">{formatMacro(monoFat)}g</span></span>}
                  {polyFat > 0 && <span>Polyunsaturated <span className="font-semibold text-slate-700">{formatMacro(polyFat)}g</span></span>}
                  {saturatedFat <= 0 && transFat <= 0 && monoFat <= 0 && polyFat <= 0 && (
                    <span>No detailed fat split available.</span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-start">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 transition-colors hover:text-slate-900"
              >
                <ArrowLeft className="h-4 w-4" />
                Go Back
              </button>
            </div>
          </div>
        </div>
      </Paper>
    </Box>
  );
};

export default AIMealPlanRecipeDetailsPage;
